import React, { useState, useEffect } from 'react'
import CustomizedSwitch from 'src/components/customized-switch'
import ContainerImageGalery from 'src/container/image-gallery'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { Icon } from '@iconify/react'
import { useCourseSetting } from 'src/hooks/useCourseSettings'

const getSelectedVideo = editor => {
  const selectedElement = editor.model.document.selection.getSelectedElement()
  return selectedElement && selectedElement.name === 'videoPlayer'
    ? selectedElement
    : null
}

const ToggleRow = ({ label, description, checked, onChange }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '8px 0',
      borderTop: '1px solid #F3F4F6',
    }}
  >
    <div>
      <div style={{ fontSize: '14px', color: '#002E47' }}>{label}</div>
      <div style={{ fontSize: '12px', color: '#637381', marginTop: '2px' }}>
        {description}
      </div>
    </div>
    <CustomizedSwitch checked={checked} onChange={onChange} />
  </div>
)

const Note = ({ children }) => (
  <div
    style={{
      fontSize: '12px',
      color: '#637381',
      background: '#F4F6F8',
      borderRadius: '8px',
      padding: '8px 12px',
      marginTop: '8px',
    }}
  >
    {children}
  </div>
)

const VideoCustomizeModal = ({ editor, dispatch }) => {
  const [autoplay, setAutoplay] = useState(false)
  const [muted, setMuted] = useState(false)
  const [poster, setPoster] = useState('')
  const [mustComplete, setMustComplete] = useState(false)
  const [skipAhead, setSkipAhead] = useState(true)
  const [hideSpeed, setHideSpeed] = useState(false)

  const { loading: courseLoading, setting: generalSetting } =
    useCourseSetting('general')
  let isSequential = false
  try {
    isSequential = Boolean(JSON.parse(generalSetting?.value || '{}').isSequential)
  } catch (e) {}

  useEffect(() => {
    const selectedElement = getSelectedVideo(editor)
    if (selectedElement) {
      setAutoplay(selectedElement.getAttribute('autoplay') || false)
      const mutedVal = selectedElement.getAttribute('muted')
      setMuted(mutedVal === true)
      setPoster(selectedElement.getAttribute('poster') || '')
      setMustComplete(selectedElement.getAttribute('mustComplete') === true)
      setSkipAhead(selectedElement.getAttribute('disableSkipAhead') !== true)
      setHideSpeed(selectedElement.getAttribute('hideSpeed') === true)
    }
  }, [editor])

  const applyChanges = (newAutoplay, newMuted, newPoster) => {
    const selectedElement = getSelectedVideo(editor)
    if (!selectedElement) return

    editor.model.change(writer => {
      // Keep every existing attribute (tracks, learner controls, ...)
      const attrs = {
        ...Object.fromEntries(selectedElement.getAttributes()),
        poster: newPoster,
        autoplay: newAutoplay,
        muted: newMuted,
      }

      const videoPlayer = writer.createElement('videoPlayer', attrs)
      editor.model.insertContent(videoPlayer)
    })
  }

  // Learner controls don't change the editing view, so a plain attribute update is enough
  const setFlag = (key, enabled) => {
    const selectedElement = getSelectedVideo(editor)
    if (!selectedElement) return

    editor.model.change(writer => {
      if (enabled) {
        writer.setAttribute(key, true, selectedElement)
      } else {
        writer.removeAttribute(key, selectedElement)
      }
    })
  }

  const handleMustCompleteToggle = () => {
    setMustComplete(!mustComplete)
    setFlag('mustComplete', !mustComplete)
  }

  const handleSkipAheadToggle = () => {
    setSkipAhead(!skipAhead)
    setFlag('disableSkipAhead', skipAhead)
  }

  const handleHideSpeedToggle = () => {
    setHideSpeed(!hideSpeed)
    setFlag('hideSpeed', !hideSpeed)
  }

  const handleAutoplayToggle = () => {
    const newAutoplay = !autoplay
    setAutoplay(newAutoplay)
    if (newAutoplay) {
      setMuted(true)
      applyChanges(newAutoplay, true, poster)
    } else {
      setMuted(false)
      applyChanges(newAutoplay, false, poster)
    }
  }

  const handleMutedToggle = () => {
    const newMuted = !muted
    setMuted(newMuted)
    applyChanges(autoplay, newMuted, poster)
  }

  const handleThumbnailUpload = () => {
    const onOk = item => {
      const itemUrl = item[0].url
      setPoster(itemUrl)
      applyChanges(autoplay, muted, itemUrl)
      dispatch(messageBoxHideAction())
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display:flex;align-items:center;justify-content:center;gap:8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24"/>
              <Typography style="margin-left:8;color:#002E47;fontWeight:600;fontSize:16;">
                Media Gallery
              </Typography>
            </div>`,
          component: <ContainerImageGalery onOk={onOk} />,
          maxHeight: '640px',
          variant: MessageBoxVariant.DRAGGABLE,
        }),
      ),
    )
  }

  return (
    <div
      style={{
        padding: '16px',
        width: '400px',
        maxWidth: 'calc(100vw - 64px)',
        boxSizing: 'border-box',
        maxHeight: 'calc(100vh - 160px)',
        overflowY: 'auto',
      }}
    >
      {/* Thumbnail Section */}
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            fontSize: '14px',
            color: '#002E47',
            marginBottom: '12px',
          }}
        >
          Thumbnail
        </div>
        <div
          onClick={handleThumbnailUpload}
          style={{
            border: '2px dashed #E0E0E0',
            borderRadius: '12px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            minHeight: '120px',
            backgroundColor: '#F9FAFB',
            backgroundImage: poster ? `url(${poster})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {!poster && (
            <>
              <Icon
                icon="mdi:image-plus-outline"
                width="32"
                height="32"
                color="#9CA3AF"
              />
              <span
                style={{
                  marginTop: '8px',
                  fontSize: '13px',
                  color: '#9CA3AF',
                }}
              >
                Upload Image
              </span>
            </>
          )}
          {poster && (
            <div
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '6px',
                padding: '4px 8px',
                color: '#fff',
                fontSize: '11px',
              }}
            >
              Change
            </div>
          )}
        </div>
      </div>

      {/* Autoplay Toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0',
          borderTop: '1px solid #F3F4F6',
        }}
      >
        <span style={{ fontSize: '14px', color: '#002E47' }}>Autoplay</span>
        <CustomizedSwitch
          checked={autoplay}
          onChange={handleAutoplayToggle}
        />
      </div>

      {/* Mute on Autoplay Toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0',
          borderTop: '1px solid #F3F4F6',
          opacity: autoplay ? 1 : 0.4,
        }}
      >
        <span style={{ fontSize: '14px', color: '#002E47' }}>
            Mute video on autoplay
          </span>
          <CustomizedSwitch
            checked={muted}
            onChange={handleMutedToggle}
            disabled={!autoplay}
          />
      </div>

      {/* Learner Controls */}
      <div style={{ marginTop: '16px' }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#002E47',
            marginBottom: '4px',
          }}
        >
          Learner controls
        </div>

        <ToggleRow
          label="Video must be completed"
          description="Learners can't continue to the next page until they finish this video."
          checked={mustComplete}
          onChange={handleMustCompleteToggle}
        />
        <ToggleRow
          label="Learners can skip ahead"
          description="When off, learners can rewind but can't jump past the part they've watched."
          checked={skipAhead}
          onChange={handleSkipAheadToggle}
        />
        <ToggleRow
          label="Hide playback speed picker"
          description="Video always plays at normal speed."
          checked={hideSpeed}
          onChange={handleHideSpeedToggle}
        />

        {mustComplete && skipAhead && (
          <Note>With skipping allowed, dragging to the end also counts as finished.</Note>
        )}
        {mustComplete && !courseLoading && !isSequential && (
          <Note>
            Learners can still skip this page from the course outline. To
            prevent this, turn on <b>Sequential pages</b> in the course’s
            Sections tab.
          </Note>
        )}
      </div>
    </div>
  )
}

export default VideoCustomizeModal
